import { Navigate } from "react-router-dom";

import {
  useAuthorization,
  useAuthorizations,
  useJobAuthorization,
} from "./providers/authorization";
import { PausedProUpsellPage, ProUpsellPage } from "./components/ProUpsell";
import { Feature, JobFeature } from "./schema";
import { MaintenancePage } from "./components/Maintenance";
import { UpsellSourcingPreferences } from "./pages/JobPage/Sourcing/components/SourcingPreferences";

import { useExperiment } from "@otta/experiments/client";

export const HasAccess = ({
  feature,
  redirect,
  children,
}: {
  feature: Feature;
  redirect?: string;
  children: React.ReactElement;
}): React.ReactElement | null => {
  const { granted, loading } = useAuthorization(feature);

  if (loading) {
    return null;
  }

  if (granted) {
    // We need to return a ReactElement or null, not ReactNode
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return null;
};

export const HasAnyAccess = ({
  featureNames,
  redirect,
  children,
}: {
  featureNames: Feature[];
  redirect?: string;
  children: React.ReactElement;
}): React.ReactElement | null => {
  const { features, loading } = useAuthorizations(featureNames);

  const granted = Array.from(features).some(([, { granted }]) => granted);

  if (loading) {
    return null;
  }

  if (granted) {
    // We need to return a ReactElement or null, not ReactNode
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return null;
};

export const HasJobAccess = ({
  feature,
  children,
  noAccessRedirect,
}: {
  feature: JobFeature;
  children: React.ReactNode;
  noAccessRedirect?: string;
}): React.ReactElement | null => {
  const { granted, message, loading } = useJobAuthorization(feature);

  if (loading) {
    return null;
  }

  if (granted) {
    // We need to return a ReactElement or null, not ReactNode
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (message === "upsell") {
    if (feature === JobFeature.Sourcing) {
      return <UpsellSourcingPreferences />;
    }
    return <ProUpsellPage />;
  }

  if (message === "maintenance") {
    return <MaintenancePage />;
  }

  if (message === "paused") {
    return <PausedProUpsellPage />;
  }

  if (noAccessRedirect) {
    return <Navigate to={noAccessRedirect} replace />;
  }

  return null;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HasExperimentAccess = ({
  experiment,
  redirect,
  children,
}: {
  experiment: string;
  redirect?: string;
  children: React.ReactElement;
}): React.ReactElement | null => {
  const { variant } = useExperiment(experiment);
  const granted = variant === "variant";

  if (granted) {
    // We need to return a ReactElement or null, not ReactNode
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  return null;
};
